import {Component, EventEmitter, Input, Optional, Output} from '@angular/core';
import {
  ApplicationFormSettings,
  Competitor,
  CompetitorFieldGroup,
  CompetitorFormHelper,
  CompetitorViewContext,
  CompetitorViewData,
  CompetitorViewDataAvailableFields,
  CompetitorViewHelper,
  Country,
  EventItem,
  Lookups,
  Race,
  RaceAdditionalPricingItem,
  RaceCommercialItem,
  RaceSettings,
  TeamType,
  TOAST_STATE
} from "@model";
import {NgbActiveModal, NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {FormBuilder, FormGroup} from "@angular/forms";
import {AbstractComponent, CompetitorTableAction, OnEditCompetitor} from "@components";
import {CodebookService, CompetitorService, LoaderService, ManageTimingDataResponse, ToastService} from "@services";
import {TranslocoService} from "@ngneat/transloco";
import {LookupTeamMemberLists} from "../team-members-management-form/team-members-management-form.component";

@Component({
  selector: 'app-team-members-management',
  templateUrl: './team-members-management.component.html',
  styleUrl: './team-members-management.component.scss',
  standalone: false
})
export class TeamMembersManagementComponent extends AbstractComponent {

  @Input() race: Race | undefined;
  @Input({required: true}) eventItem: EventItem | undefined;
  @Input() competitor: Competitor | undefined;

  @Input() raceSettings: RaceSettings | undefined;
  @Input() applicationFormSettings: ApplicationFormSettings | undefined;
  @Input() raceCommercialItems: RaceCommercialItem[] = [];
  @Input() raceAdditionalItems: RaceAdditionalPricingItem[] = [];

  @Input() title: string | undefined;
  @Input() currencies: Lookups[] = [];
  @Input() countries: Country[] = [];
  @Input('sizes') tshirtSizes: Lookups[] = [];
  @Input() disciplines: Lookups[] = [];
  @Input() meals: Lookups[] = [];
  @Input() transports: Lookups[] = [];
  @Input() genders: Lookups[] = [];
  @Input() formType: CompetitorFieldGroup = CompetitorFieldGroup.GENERAL;
  @Output() onSaveSuccessAction: EventEmitter<Competitor> = new EventEmitter<Competitor>();

  private static readonly IGNORED_TEAM_VIEW_COLUMN_KEYS: (keyof CompetitorViewData)[] = [
    'id', 'created', 'modified'
  ];
  private static readonly START_INFO_TEAM_VIEW_COLUMN_KEYS: (keyof CompetitorViewData)[] = [
    'fullName', 'email', 'sex', 'chipNumber', 'startNumber', 'startNumberExtension'
  ];

  filteredLists: LookupTeamMemberLists = new LookupTeamMemberLists();

  formHelper: CompetitorFormHelper | undefined;
  viewHelper: CompetitorViewHelper | undefined;
  inputForm: FormGroup = new FormGroup({});
  editingTeamMemberId: number | undefined;
  teamMemberFields?: CompetitorViewDataAvailableFields;
  teamMemberViewData: CompetitorViewData[] = [];

  activeTeamMemberForm: FormGroup | undefined;
  activeTeamMemberFormHelper: CompetitorFormHelper | undefined;

  invalidTeamMemberIds: Record<number, boolean> = {};
  hasInvalidTeamMembers = false;

  timingDataError: boolean = false;
  timingDataResponse: ManageTimingDataResponse | undefined;

  isTeamRace: boolean = false;

  private readonly teamMemberForms = new Map<number, FormGroup>();
  private readonly teamMemberFormHelpers = new Map<number, CompetitorFormHelper>();

  constructor(@Optional() public activeModal: NgbActiveModal, public translocoService: TranslocoService, public formBuilder: FormBuilder,
              private codebookService: CodebookService, private modalService: NgbModal, private loaderService: LoaderService,
              private competitorService: CompetitorService, private toastService: ToastService
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToCodebookValues();
    this.filterInputLists();

    if (this.race?.id && this.competitor && this.eventItem?.id) {
      this.isTeamRace = !!this.raceSettings?.teamType && this.raceSettings.teamType !== TeamType.SINGLE;
      this.formHelper = new CompetitorFormHelper({
        competitor: this.competitor,
        applicationFormSettings: this.applicationFormSettings,
        raceSettings: this.raceSettings,
        context: CompetitorViewContext.ADMINISTRATION,
        forSingleCompetitor: !this.isTeamRace,
        itemsSource: {
          sex: this.filteredLists.genders,
          country: this.filteredLists.countries,
          nationality: this.filteredLists.countries,
          tshirt: this.filteredLists.tshirtSizes,
          meal: this.filteredLists.meals,
          transport: this.filteredLists.transports,
          discipline: this.filteredLists.disciplines,
        },
        formType: this.formType,
        dateBounds: {
          // TODO: wire minDateForBirthday/calendarNavigationDate through if this component has
          // access to the same bounds the registration form uses — not available here yet.
          currentDate: new Date(),
        },
      });
      this.inputForm = this.formHelper.buildFormGroup();
      if (this.isTeamRace) {
        const raceId = this.race.id;
        const raceSettingsMap = new Map<number, RaceSettings>();
        const raceMap = new Map<number, Race>();
        const applicationFormSettingsMap = new Map<number, ApplicationFormSettings>();
        const additionalPricingItemsMap = new Map<number, RaceAdditionalPricingItem[]>();
        const commercialItemsMap = new Map<number, RaceCommercialItem[]>();
        if (this.raceSettings) raceSettingsMap.set(raceId, this.raceSettings);
        if (this.applicationFormSettings) applicationFormSettingsMap.set(raceId, this.applicationFormSettings);
        if (this.raceAdditionalItems?.length) additionalPricingItemsMap.set(raceId, this.raceAdditionalItems);
        if (this.raceCommercialItems?.length) commercialItemsMap.set(raceId, this.raceCommercialItems);
        raceMap.set(raceId, this.race)
        this.viewHelper = new CompetitorViewHelper(
            this.eventItem.id,
            this.translocoService,
            this.filteredLists.countries,
            applicationFormSettingsMap,
            this.eventItem.paymentSettings,
            raceSettingsMap,
            commercialItemsMap,
            additionalPricingItemsMap,
            CompetitorViewContext.ADMINISTRATION,
            false,
            raceMap,
            this.race
        );
        const raceFields = this.viewHelper.viewDataByRaceIds.get(raceId);
        if (raceFields?.teamMemberView && this.competitor.teamMembers) {
          this.teamMemberFields = raceFields.teamMemberView;
          if (this.formType === CompetitorFieldGroup.START_INFO) {
            this.viewHelper.selectFields(TeamMembersManagementComponent.START_INFO_TEAM_VIEW_COLUMN_KEYS, raceFields.teamMemberView);
          }
          else {
            this.viewHelper.selectAllExcept(this.teamMemberFields, TeamMembersManagementComponent.IGNORED_TEAM_VIEW_COLUMN_KEYS);
          }
          this.competitor.teamMembers.forEach(tm => {
            this.teamMemberViewData.push(this.viewHelper!.competitorToCompetitorViewData(tm, false));

            if (tm.id == null) return;
            const memberId = tm.id;
            const memberFormHelper = this.formHelper!.forTeamMember(tm);
            if (!memberFormHelper) return;

            const memberForm = memberFormHelper.buildFormGroup();
            this.teamMemberFormHelpers.set(memberId, memberFormHelper);
            this.teamMemberForms.set(memberId, memberForm);
            this.invalidTeamMemberIds[memberId] = memberForm.invalid;
            this.recomputeHasInvalidTeamMembers();

            this.subscriptions.push(
                memberForm.statusChanges.subscribe(() => {
                  this.invalidTeamMemberIds = {...this.invalidTeamMemberIds, [memberId]: memberForm.invalid};
                  this.recomputeHasInvalidTeamMembers();
                })
            );
          })
        }
      }
    }
  }

  subscribeToCodebookValues() {
    this.subscriptions.push(this.codebookService.disciplines.value.subscribe(val => {
          this.disciplines = val;
        }),
        this.codebookService.sizes.value.subscribe(val => {
          this.tshirtSizes = val;
        }),
        this.codebookService.meals.value.subscribe(val => {
          this.meals = val;
        }),
        this.codebookService.transports.value.subscribe(val => {
          this.transports = val;
        }),
        this.codebookService.countries.value.subscribe(val => {
          this.countries = val;
        }),
        this.codebookService.currencies.value.subscribe(val => {
          this.currencies = val;
        }),
        this.codebookService.genders.value.subscribe(val => {
          this.genders = val;
        })
    )
  }


  filterInputLists() {
    if ((this.race?.applicationFormSettings?.meals?.length ?? 0) > 0) {
      this.filteredLists.meals = this.filterList(this.race?.applicationFormSettings?.meals, this.meals);
    }
    if ((this.race?.applicationFormSettings?.tshirts?.length ?? 0) > 0) {
      this.filteredLists.tshirtSizes = this.filterList(this.race?.applicationFormSettings?.tshirts, this.tshirtSizes);
    }
    if ((this.race?.applicationFormSettings?.disciplines?.length ?? 0) > 0) {
      this.filteredLists.disciplines = this.filterList(this.race?.applicationFormSettings?.disciplines, this.disciplines);
    }
    this.filteredLists.genders = [...this.genders];
    this.filteredLists.countries = [...this.countries];
    this.filteredLists.transports = [...this.transports];
    this.filteredLists.currencies = [...this.currencies];
  }

  filterList(ids: number[] | undefined, list: Lookups[] | undefined): Lookups[] {
    let filteredItems: Lookups[] = [];
    if ((ids?.length ?? 0) > 0 && (list?.length ?? 0) > 0) {
      for (let item of list!) {
        for (let id of ids!) {
          if (item.id === id) {
            filteredItems.push(item);
          }
        }
      }
    }
    return filteredItems;
  }

  private recomputeHasInvalidTeamMembers(): void {
    this.hasInvalidTeamMembers = Object.values(this.invalidTeamMemberIds).some((invalid) => invalid);
  }

  onCancel(): void {
    this.activeModal?.dismiss();
    //todo reset
  }

  get editingTeamMemberFullName(): string {
    const teamMember = this.competitor?.teamMembers?.find((tm) => tm.id === this.editingTeamMemberId);
    if (!teamMember) return '';
    return `${teamMember.firstName ?? ''} ${teamMember.lastName ?? ''}`.trim();
  }

  onTeamMemberEdit(teamMemberView: CompetitorViewData): void {
    if (teamMemberView.id == null || teamMemberView.id === this.editingTeamMemberId) {
      return;
    }

    this.applyActiveTeamMemberChanges();

    const nextId = teamMemberView.id;
    this.editingTeamMemberId = undefined;
    this.activeTeamMemberForm = undefined;
    this.activeTeamMemberFormHelper = undefined;

    setTimeout(() => {
      this.editingTeamMemberId = nextId;
      this.activeTeamMemberForm = this.teamMemberForms.get(nextId);
      this.activeTeamMemberFormHelper = this.teamMemberFormHelpers.get(nextId);
    });
  }


  onTeamMemberEditClose(): void {
    this.applyActiveTeamMemberChanges();
    this.editingTeamMemberId = undefined;
    this.activeTeamMemberForm = undefined;
    this.activeTeamMemberFormHelper = undefined;
  }

  /** Reads the currently-open team member's form and merges any changes into `competitor.teamMembers`. */
  private applyActiveTeamMemberChanges(): void {
    if (this.editingTeamMemberId == null || !this.activeTeamMemberForm || !this.activeTeamMemberFormHelper) {
      return;
    }

    this.checkValidators(this.activeTeamMemberForm);
    const updatedFields = this.activeTeamMemberFormHelper.getFormData(this.activeTeamMemberForm);
    this.mergeTeamMemberFields(this.editingTeamMemberId, updatedFields);
  }

  private mergeTeamMemberFields(id: number, updatedFields: Partial<Competitor>): void {
    const teamMembers = this.competitor?.teamMembers;
    if (!teamMembers) return;

    const index = teamMembers.findIndex((tm) => tm.id === id);
    if (index === -1) return;

    const updatedTeamMember: Competitor = {...teamMembers[index], ...updatedFields};

    const updatedTeamMembers = [...teamMembers];
    updatedTeamMembers[index] = updatedTeamMember;
    this.competitor = {...this.competitor!, teamMembers: updatedTeamMembers};

    if (this.viewHelper) {
      const viewIndex = this.teamMemberViewData.findIndex((tm) => tm.id === id);
      if (viewIndex !== -1) {
        this.teamMemberViewData[viewIndex] = this.viewHelper.competitorToCompetitorViewData(updatedTeamMember, false);
      }
    }
  }

  onCompetitorSave() {
    this.applyActiveTeamMemberChanges();

    if (!this.formHelper) return;

    let firstInvalidTeamMemberId: number | undefined;
    this.teamMemberForms.forEach((form, id) => {
      form.markAllAsTouched();
      this.invalidTeamMemberIds = {...this.invalidTeamMemberIds, [id]: form.invalid};
      if (form.invalid && firstInvalidTeamMemberId == null) {
        firstInvalidTeamMemberId = id;
      }
    });
    this.recomputeHasInvalidTeamMembers();

    if (!this.inputForm.valid || firstInvalidTeamMemberId != null) {
      for (const control in this.inputForm.controls) {
        this.inputForm.get(control)?.markAsTouched();
      }

      if (firstInvalidTeamMemberId != null) {
        const invalidId = firstInvalidTeamMemberId;
        this.editingTeamMemberId = undefined;
        this.activeTeamMemberForm = undefined;
        this.activeTeamMemberFormHelper = undefined;
        setTimeout(() => {
          this.editingTeamMemberId = invalidId;
          this.activeTeamMemberForm = this.teamMemberForms.get(invalidId);
          this.activeTeamMemberFormHelper = this.teamMemberFormHelpers.get(invalidId);
        });
      }
      return;
    }
    this.checkValidators(this.inputForm);
    const updatedFields = this.formHelper.getFormData(this.inputForm);
    const updatedCompetitor: Competitor = {...this.competitor, ...updatedFields};

    this.loaderService.startLoading();
    if (this.formType === CompetitorFieldGroup.START_INFO) {
      this.competitorService.manageCompetitorTimingData(updatedCompetitor, () => {
          this.competitorService.fetchCompetitorById(this.competitor!.id!).subscribe(competitor => {
            if (competitor) {
              this.onSaveSuccess(competitor);
            }
          })
          }, (error?: ManageTimingDataResponse) => {
              this.loaderService.dismissLoading();
              this.timingDataError = true;
              this.timingDataResponse = error;
          }
      )
    }
    else {
      this.competitorService.manageCompetitor(updatedCompetitor, (result: Competitor) => {
        this.onSaveSuccess(result)
      }, (errorCode?: number) => {
        this.loaderService.dismissLoading();
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(
            "competitor.edit.error." + errorCode));
      });
    }
  }

  onSaveSuccess(competitor: Competitor) {
    this.onSaveSuccessAction.emit(competitor);
    this.loaderService.dismissLoading();
    this.activeModal?.dismiss();
  }


  keepOriginalOrder = () => 0;

  protected readonly TeamType = TeamType;
  protected readonly CompetitorTableAction = CompetitorTableAction;
}